import {Three, Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {getJitterVector} from "@app/template-editor/helpers/jitter"
import {getApertureVector} from "@app/template-editor/helpers/aperture"
import {HELPER_OBJECTS_LAYER, OVERLAY_OBJECTS_LAYER} from "@app/template-editor/helpers/helper-objects"

export type DepthOfField = {
    apertureSize: number
    focalDistance: number
}

export type ThreeImageData = {
    buffer: THREE.TypedArray
    width: number
    height: number
    format: THREE.PixelFormat
    type: THREE.TextureDataType
    premultipliedAlpha: boolean
}

export class SceneViewRenderPass extends THREE.TempNode<"vec4"> {
    private beautyNode: THREE.PassNode
    private sceneDepthNode: THREE.PassNode
    private overlayNode: THREE.PassNode
    private imageTexture: THREE.DataTexture
    private useImageTexture = TSL.uniform(false)
    private historyRenderTarget: THREE.RenderTarget
    private resolveRenderTarget: THREE.RenderTarget
    private crossfadeSourceRenderTarget: THREE.RenderTarget
    private textureNode: ReturnType<typeof TSL.passTexture>
    private resolveMaterial: THREE.NodeMaterial
    private quadMesh = new THREE.QuadMesh()
    private taaFrame = TSL.uniform(-1)
    private crossfadeAlphaUniform = TSL.uniform(1.0)
    private pendingCrossfadeCapture = false
    private depthOfField: DepthOfField | undefined
    private renderHelperObjects: boolean = false
    private jitterCamera: Three.PerspectiveCamera = new THREE.PerspectiveCamera()
    private jitterCameraSceneDepth: Three.PerspectiveCamera = new THREE.PerspectiveCamera()
    private jitterCameraOverlay: Three.PerspectiveCamera = new THREE.PerspectiveCamera()

    constructor(
        private scene: THREE.Scene,
        private camera: THREE.PerspectiveCamera,
    ) {
        super("vec4")
        this.beautyNode = TSL.pass(this.scene, this.jitterCamera)
        this.sceneDepthNode = TSL.depthPass(this.scene, this.jitterCameraSceneDepth)
        this.overlayNode = TSL.pass(this.scene, this.jitterCameraOverlay)

        this.imageTexture = new THREE.DataTexture(new Uint8Array(4), 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType)
        this.imageTexture.minFilter = THREE.NearestFilter
        this.imageTexture.magFilter = THREE.NearestFilter
        this.imageTexture.wrapS = THREE.ClampToEdgeWrapping
        this.imageTexture.wrapT = THREE.ClampToEdgeWrapping

        this.historyRenderTarget = new THREE.RenderTarget(1, 1, {depthBuffer: false, type: THREE.HalfFloatType})
        this.historyRenderTarget.texture.name = "SceneViewRenderPass.history"

        this.resolveRenderTarget = new THREE.RenderTarget(1, 1, {depthBuffer: false, type: THREE.HalfFloatType, count: 2})
        this.resolveRenderTarget.textures[0].name = "resolve"
        this.resolveRenderTarget.textures[1].name = "crossfaded"

        this.crossfadeSourceRenderTarget = new THREE.RenderTarget(1, 1, {depthBuffer: false, type: THREE.HalfFloatType})
        this.crossfadeSourceRenderTarget.texture.name = "SceneViewRenderPass.crossfadeSource"

        //@ts-expect-error passTexture wants a PassNode, not a TempNode
        this.textureNode = TSL.passTexture(this, this.resolveRenderTarget.textures[1])

        this.resolveMaterial = new THREE.NodeMaterial()
        this.resolveMaterial.name = "SceneViewRenderPass.resolve"

        this.updateBeforeType = THREE.NodeUpdateType.FRAME
    }

    override updateBefore(frame: THREE.NodeFrame): undefined {
        const {renderer} = frame
        if (!renderer) return

        const beautyRenderTarget = this.beautyNode.renderTarget

        const width = beautyRenderTarget.texture.width
        const height = beautyRenderTarget.texture.height

        const needsRestart =
            this.resolveRenderTarget.width !== width ||
            this.resolveRenderTarget.height !== height ||
            this.historyRenderTarget.width !== width ||
            this.historyRenderTarget.height !== height
        this.historyRenderTarget.setSize(width, height)
        this.resolveRenderTarget.setSize(width, height)
        this.crossfadeSourceRenderTarget.setSize(width, height)

        const rendererState = THREE.RendererUtils.saveRendererState(renderer)

        if (needsRestart === true) {
            renderer.setRenderTarget(this.historyRenderTarget)
            renderer.clear()

            renderer.setRenderTarget(this.resolveRenderTarget)
            renderer.clear()

            renderer.copyTextureToTexture(beautyRenderTarget.texture, this.historyRenderTarget.texture)

            // Force GPU texture allocation at the correct size for the new render target
            renderer.setRenderTarget(this.crossfadeSourceRenderTarget)
            renderer.clear()

            // End any active crossfade on resize — source content is now stale
            this.crossfadeAlphaUniform.value = 1.0
            this.pendingCrossfadeCapture = false
        }

        // Capture current TAA output as crossfade source if requested
        if (this.pendingCrossfadeCapture) {
            renderer.copyTextureToTexture(this.resolveRenderTarget.textures[0], this.crossfadeSourceRenderTarget.texture)
            this.pendingCrossfadeCapture = false
        }

        renderer.setRenderTarget(this.resolveRenderTarget)
        this.quadMesh.material = this.resolveMaterial
        this.quadMesh.name = "SceneViewRenderPass"
        renderer.clear()
        this.quadMesh.render(renderer)

        renderer.copyTextureToTexture(this.resolveRenderTarget.textures[0], this.historyRenderTarget.texture)

        THREE.RendererUtils.restoreRendererState(renderer, rendererState)
    }

    override setup(builder: THREE.NodeBuilder) {
        const renderPipeline = builder.getContext().renderPipeline as
            | {
                  context: {
                      onBeforeRenderPipeline: (() => void) | null
                      onAfterRenderPipeline: (() => void) | null
                  }
              }
            | undefined

        if (renderPipeline) {
            const onBeforeRenderPipeline = renderPipeline.context.onBeforeRenderPipeline
            renderPipeline.context.onBeforeRenderPipeline = () => {
                this.jitterCamera.copy(this.camera, true)
                if (this.renderHelperObjects) {
                    this.jitterCamera.layers.enable(HELPER_OBJECTS_LAYER)
                    this.jitterCamera.layers.enable(OVERLAY_OBJECTS_LAYER)
                } else {
                    this.jitterCamera.layers.disable(HELPER_OBJECTS_LAYER)
                    this.jitterCamera.layers.disable(OVERLAY_OBJECTS_LAYER)
                }

                if (this.useImageTexture.value) this.jitterCamera.layers.disable(0)
                else this.jitterCamera.layers.enable(0)

                if (this.taaFrame.value >= 0) {
                    const [offsetX, offsetY] = getJitterVector(this.taaFrame.value)

                    const beautyRenderTarget = this.beautyNode.renderTarget

                    const width = beautyRenderTarget.texture.width
                    const height = beautyRenderTarget.texture.height

                    offsetCameraView(this.jitterCamera, width, height, offsetX, offsetY)

                    if (this.depthOfField) {
                        const {apertureSize, focalDistance} = this.depthOfField
                        const [apertureX, apertureY] = getApertureVector(this.taaFrame.value)
                        const apertureM = lensApertureOffsetMatrix(apertureX * apertureSize, apertureY * apertureSize, focalDistance)
                        multiplyCameraMatrix(this.jitterCamera, apertureM)
                    }
                }

                if (this.useImageTexture.value) {
                    this.jitterCameraSceneDepth.copy(this.jitterCamera, true)
                    this.jitterCameraSceneDepth.layers.set(0)

                    this.jitterCameraOverlay.copy(this.jitterCamera, true)
                    this.jitterCameraOverlay.layers.set(OVERLAY_OBJECTS_LAYER)
                } else {
                    this.jitterCameraSceneDepth.layers.disableAll()
                    this.jitterCameraOverlay.layers.disableAll()
                }

                if (onBeforeRenderPipeline) onBeforeRenderPipeline()
            }
        }

        const beautyTexture = this.beautyNode.getTextureNode()
        const beautyDepthTexture = this.beautyNode.getTextureNode("depth")
        const sceneDepthTexture = this.sceneDepthNode.getTextureNode("depth")
        const overlayTexture = this.overlayNode.getTextureNode()
        const historyNode = TSL.texture(this.historyRenderTarget.texture)
        const imageTextureNode = TSL.texture(this.imageTexture)
        const crossfadeSourceNode = TSL.texture(this.crossfadeSourceRenderTarget.texture)

        const resolve = TSL.Fn(() => {
            const uvNode = TSL.uv()
            const backgroundColor = TSL.select(this.useImageTexture, imageTextureNode.sample(uvNode), TSL.vec4(0.0))
            const sceneDepth = sceneDepthTexture.sample(uvNode).r
            const beautyDepth = beautyDepthTexture.sample(uvNode).r
            const beautyColor = TSL.select(beautyDepth.lessThanEqual(sceneDepth), beautyTexture.sample(uvNode), overlayTexture.sample(uvNode))
            const oneMinusAlpha = TSL.sub(1.0, beautyColor.a)
            const blendedColor = TSL.add(beautyColor, TSL.mul(backgroundColor, oneMinusAlpha))

            const result = TSL.vec4(0.0).toVar()

            TSL.If(this.taaFrame.greaterThan(0.0), () => {
                const historyColor = historyNode.sample(uvNode).mul(this.taaFrame)
                const blended = TSL.add(blendedColor, historyColor).div(TSL.add(1.0, this.taaFrame))
                result.assign(blended)
            }).Else(() => {
                result.assign(blendedColor)
            })

            return result
        })

        const taaResult = resolve()
        const crossfadedResult = TSL.mix(crossfadeSourceNode.sample(TSL.uv()), taaResult, this.crossfadeAlphaUniform)

        this.resolveMaterial.mrtNode = TSL.mrt({
            resolve: taaResult,
            crossfaded: crossfadedResult,
        })

        return this.textureNode
    }

    override dispose() {
        this.beautyNode.dispose()
        this.sceneDepthNode.dispose()
        this.overlayNode.dispose()
        this.imageTexture.dispose()

        this.historyRenderTarget.dispose()
        this.resolveRenderTarget.dispose()
        this.crossfadeSourceRenderTarget.dispose()

        this.resolveMaterial.dispose()
    }

    setTaaFrame(taaFrame: number | null) {
        this.taaFrame.value = taaFrame ?? -1
    }

    captureCrossfadeSource() {
        this.pendingCrossfadeCapture = true
    }

    setCrossfadeAlpha(alpha: number) {
        this.crossfadeAlphaUniform.value = alpha
    }

    getTaaFrame() {
        return this.taaFrame.value >= 0 ? this.taaFrame.value : null
    }

    setDepthOfField(depthOfField: DepthOfField | undefined) {
        this.depthOfField = depthOfField
    }

    setRenderHelperObjects(renderHelperObjects: boolean) {
        this.renderHelperObjects = renderHelperObjects
    }

    setImageData(data: ThreeImageData | null) {
        this.useImageTexture.value = data !== null

        this.imageTexture.dispose()
        this.imageTexture.needsUpdate = true

        if (!data) {
            this.imageTexture.image = {
                data: new Uint8Array(4),
                width: 1,
                height: 1,
            }
            this.imageTexture.format = THREE.RGBAFormat
            this.imageTexture.type = THREE.UnsignedByteType
            this.imageTexture.premultiplyAlpha = true

            return
        }

        this.imageTexture.image = {
            data: data.buffer,
            width: data.width,
            height: data.height,
        }
        this.imageTexture.format = data.format
        this.imageTexture.type = data.type
        this.imageTexture.premultiplyAlpha = !data.premultipliedAlpha // convert to premultiplied alpha for the shader
    }
}

function offsetCameraView(camera: THREE.PerspectiveCamera, fullWidth: number, fullHeight: number, offsetX: number, offsetY: number) {
    const origView = camera.view?.enabled ? camera.view : null
    // override camera.setViewOffset(...)
    camera.view = {
        enabled: true,
        fullWidth,
        fullHeight,
        offsetX: offsetX + (origView ? origView.offsetX * (fullWidth / origView.fullWidth) : 0),
        offsetY: offsetY + (origView ? origView.offsetY * (fullHeight / origView.fullHeight) : 0),
        width: fullWidth,
        height: fullHeight,
    }
    camera.updateProjectionMatrix()
}

function lensApertureOffsetMatrix(ax: number, ay: number, focalDistance: number): THREE.Matrix4 {
    const lensOfs = new THREE.Vector3(ax, ay, 0)
    const m = new THREE.Matrix4().lookAt(lensOfs, new THREE.Vector3(0, 0, -focalDistance), new THREE.Vector3(0, 1, 0))
    return new THREE.Matrix4().makeTranslation(lensOfs.x, lensOfs.y, 0).multiply(m)
}

function multiplyCameraMatrix(camera: THREE.Camera, m: THREE.Matrix4) {
    camera.updateMatrixWorld()
    camera.matrixWorld.multiply(m)
    camera.matrixWorld.decompose(camera.position, camera.quaternion, camera.scale)
}
